import React, { Fragment, useEffect } from 'react';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import loadable from '@loadable/component';
import { isAfter } from 'date-fns';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Constrain, Stack } from '@moda/om';
import { compact, SECONDS_TO_MILLISECONDS } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { usePLPTracking } from '../../hooks/usePLPTracking';
import { REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS } from '../../constants';
import {
  TrunkshowPageQuery,
  TrunkshowPageQueryVariables,
  CarouselCellFragment,
  VariantCellFragment
} from '../../generated/types';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import {
  useHasAccessToProductOrTrunkshow,
  HAS_ACCESS_TO_TRUNKSHOW_FRAGMENT
} from '../../hooks/useHasAccessToProductOrTrunkshow';
import { useTrunkshowTitle, TRUNKSHOW_TITLE_FRAGMENT } from '../../hooks/useTrunkshowTitle';
import { EnableLazyLoad } from '../../components/LazyLoad';
import { Loading } from '../../components/Loading';
import { CAROUSEL_PUMO_MODULE_FRAGMENT } from '../../components/CarouselPumoModule';
import { PageMeta } from '../../components/PageMeta';
import { useCountry } from '../../components/CountrySelector';
import { NotFoundPage } from '../NotFoundPage';
import { TrunkshowEndedHeader } from '../../components/TrunkshowEndedHeader';
import { useUrlParams } from '../../hooks/useUrlParams';
import { LooksModule, getLooksModuleData } from '../../components/LooksModule';
import { TrunkshowPageSubtitle } from '../../components/TrunkshowPageSubtitle';
import { PdpTrunkshowInfo } from '../../components/PdpInfoContainer/PdpTrunkshowInfo';
import { usePageType } from '../../hooks/usePageType';
import { PumoModule } from '../../components/PumoModule';
import { VIDEO_PUMO_MODULE_FRAGMENT } from '../../components/VideoPumoModule';
import { EditorialVideo } from '../../components/EditorialVideo';
import {
  TRUNKSHOW_HEADER_MODULE_PUMO_FRAGMENT,
  TRUNKSHOW_HEADER_MODULE_TRUNKSHOW_FRAGMENT
} from './TrunkshowHeaderModule';

import { RedirectExpiredTrunkshow } from './RedirectExpiredTrunkshow';

import './TrunkshowPage.scss';

const TrunkshowHeaderModule = loadable(() => import('./TrunkshowHeaderModule'), {
  resolveComponent: exports => exports.TrunkshowHeaderModule
});

const TRUNKSHOW_PAGE_QUERY: TypedDocumentNode<TrunkshowPageQuery, TrunkshowPageQueryVariables> =
  gql`
    query TrunkshowPageQuery(
      $slug: String!
      $countryCode: String
      $versionId: Int
      $ruleSuffix: String
    ) {
      trunkshowPage(
        slug: $slug
        country_code: $countryCode
        version_id: $versionId
        rule_suffix: $ruleSuffix
      ) {
        displayPage {
          targetBuckets {
            name
            modules {
              ...CarouselPumoModuleFragment
              ...TrunkshowHeaderModulePumoFragment
              ...VideoPumoModuleFragment
            }
          }
        }
        trunkshow {
          name
          designerNames: designer_names
          designerSlugs: designer_slugs
          designersData: limited_designer_data {
            id
            name
            slug
          }
          seasonName: season_name
          activeEndI: active_end_i
          ...HasAccessToTrunkshowFragment
          ...TrunkshowHeaderModuleTrunkshowFragment
          ...TrunkshowTitleFragment
        }
      }
    }
    ${HAS_ACCESS_TO_TRUNKSHOW_FRAGMENT}
    ${TRUNKSHOW_HEADER_MODULE_PUMO_FRAGMENT}
    ${TRUNKSHOW_HEADER_MODULE_TRUNKSHOW_FRAGMENT}
    ${CAROUSEL_PUMO_MODULE_FRAGMENT}
    ${TRUNKSHOW_TITLE_FRAGMENT}
    ${VIDEO_PUMO_MODULE_FRAGMENT}
  `;

export const TrunkshowPage: React.FC = () => {
  const location = useLocation();
  const {
    trunkshowSlug = '',
    designerSlug = '',
    season = '',
    vertical = 'women'
  } = useParams<{
    trunkshowSlug?: string;
    designerSlug?: string;
    season?: string;
    vertical?: string;
  }>();

  const {
    queryParams: { version_id: versionId }
  } = useUrlParams();

  const { country } = useCountry();
  const slug = trunkshowSlug || `${designerSlug}-${season}`;

  const { data, loading, error } = useQuery(TRUNKSHOW_PAGE_QUERY, {
    variables: {
      slug,
      countryCode: country.alpha2Code,
      ...(versionId && { versionId: Number(versionId) })
    }
  });

  const { hasAccess, loading: loadingAccess } = useHasAccessToProductOrTrunkshow(
    data?.trunkshowPage?.trunkshow
  );

  const trunkshow = data?.trunkshowPage?.trunkshow;
  const displayPage = data?.trunkshowPage?.displayPage;
  const title = useTrunkshowTitle(trunkshow);
  const description = `Preorder the ${title} on Moda Operandi.`;

  if (error) throwQueryError(error);

  const headerModules = compact(
    displayPage?.targetBuckets.find(({ name }) => name === 'header')?.modules
  );

  const variantCarousels = compact(
    displayPage?.targetBuckets.find(({ name }) => name === 'system_products')?.modules
  );

  const cells = variantCarousels.reduce((collectedCells, carousel) => {
    if (carousel?.__typename === 'CarouselPumoModule') {
      return collectedCells.concat(carousel.cells.filter(cell => cell.__typename === 'Variant'));
    }
    return collectedCells;
  }, [] as CarouselCellFragment[]);

  usePLPTracking({
    variants: cells as VariantCellFragment[],
    title: title
  });

  const pageType = usePageType();

  useEffect(() => {
    if (pageType === 'TSP') {
      localStorage.setItem('TSPVisited', 'true');
    }
  }, [pageType]);

  if (loading || !data || loadingAccess) return <Loading />;

  if (!trunkshow) return <NotFoundPage />;

  // if the user does not have access to the trunkshow return a 404
  if (!hasAccess) return <NotFoundPage />;

  const hasExpiredAlongTimeAgo = trunkshow.activeEndI
    ? isAfter(
        new Date(),
        trunkshow.activeEndI * SECONDS_TO_MILLISECONDS + REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS
      )
    : false;

  // if the trunkshow has expired a long time ago, redirect to the designer page
  if (hasExpiredAlongTimeAgo) {
    return <RedirectExpiredTrunkshow designerSlug={trunkshow.designerSlugs?.[0]} />;
  }

  const isExpired = trunkshow.activeEndI
    ? isAfter(new Date(), trunkshow.activeEndI * SECONDS_TO_MILLISECONDS)
    : false;

  // if the url is constructed incorrectly, redirect to the correct url
  const targetPath = hrefFor.TrunkshowPage({ vertical, trunkshowSlug: slug });
  if (targetPath !== location.pathname) {
    return <Redirect to={`${targetPath}${location.search}`} />;
  }

  return (
    <>
      <PageMeta title={title} description={description} />

      <div className="TrunkshowPage">
        {isExpired && <TrunkshowEndedHeader trunkshow={trunkshow} />}

        {headerModules.map((headerModule, index) => (
          <Fragment key={index}>
            {headerModule.__typename === 'SinglePumoModule' && (
              <Constrain>
                <TrunkshowHeaderModule
                  key={headerModule.id}
                  trunkshowHeaderModulePumo={headerModule}
                  trunkshowHeaderModuleTrunkshow={trunkshow}
                  hideCtaUnderline
                />
              </Constrain>
            )}
            {headerModule.__typename === 'VideoPumoModule' && (
              <EditorialVideo key={headerModule.id} video={headerModule.video} />
            )}
          </Fragment>
        ))}

        <Constrain>
          <div className="TrunkshowPage__expandable">
            <PdpTrunkshowInfo mode="trunkshowPage" />
          </div>
        </Constrain>

        <EnableLazyLoad>
          <div className="TrunkshowPage__looks">
            <LooksModule carousels={getLooksModuleData(displayPage)} />
          </div>
          {variantCarousels.map(
            carouselPumoModule =>
              carouselPumoModule.__typename === 'CarouselPumoModule' &&
              carouselPumoModule.cells.length > 0 && (
                <Fragment key={carouselPumoModule.id}>
                  <Stack space={6}>
                    <TrunkshowPageSubtitle>Shop Products</TrunkshowPageSubtitle>

                    <PumoModule module={carouselPumoModule} />
                  </Stack>
                </Fragment>
              )
          )}
        </EnableLazyLoad>
      </div>
    </>
  );
};
